import { h, render, getCurrentInstance } from 'vue'
import { on } from 'devextreme/events'

import { getI18n } from '@/plugins/i18n'

import { useWebSiteStore } from '@/stores/web-site.store'
import { usePlanningStore } from '@/stores/planning.store'
import { useAccountingStore } from '@/stores/accounting.store'
import { usePortalStore } from '@/stores/portal.store'

import { useLookup, useCurrencyOptions, useCurrencyColumnFormatter, usePercentageOptions, useODataSourceOptions } from '@/composables/data-grid'

import { useArchive } from '@/composables/archive'
import { usePriceService } from '@/composables/price.service'
import { useCurrency } from '@/composables/currency'
import { promptCurrencyChange } from '@/composables/exchange-rate'
import { navigateToBudgetDocument, navigateToCompany, navigateToExpenseClaim, navigateToInvoice, navigateToListingItem, navigateToProject } from '@/composables/navigator'
import { useNumberComparison } from '@/composables/number-comparison'

import router, { menuRoutes, menuRouteEnum } from '@/router'
import { formatShortDate } from '@/lib/utils'

import InformationTooltip from '@/components/shared/InformationTooltip.vue'
import ServiceLinkTemplate from '@/components/shared/ServiceLinkTemplate.vue'

export const useAvatarColumn = (options) => ({
  dataField: 'avatar_file',
  caption: '',
  allowSorting: false,
  allowFiltering: false,
  allowExporting: false,
  showInColumnChooser: false,
  width: '3rem',
  cellTemplate: (element, { data }) => {
    const src = (options?.avatar_file_field ? data[options.avatar_file_field] : data.avatar_file) || (options?.avatar_url_field ? data[options.avatar_url_field] : data.avatar_url)
    if (!src) return
    const img = document.createElement('img')
    img.onerror = () => img.remove()
    img.src = src
    img.style.cssText = 'width:2rem;height:2rem;object-fit:cover;border-radius:50%;display:block;margin:auto'
    element.appendChild(img)
  },
  ...options,
})

export const useArchivedAtColumn = (options) => {
  const i18n = getI18n()

  return {
    caption: i18n.t('common.archivedAt'),
    dataField: 'archived_at',
    dataType: 'date',
    ...options,
  }
}

export const useArchivedColumn = (options) => {
  const i18n = getI18n()

  return {
    caption: i18n.t('common.archived'),
    dataField: 'archived',
    dataType: 'boolean',
    width: '6rem',
    ...options
  }
}

export const useChargePercentageColumn = (options) => {
  const i18n = getI18n()
  const { setChargePercentageValue } = usePriceService()

  return {
    caption: i18n.t('common.chargePercentage'),
    width: '5rem',
    ...usePercentageOptions(),
    dataField: 'charge_percentage',
    dataType: 'number',
    setCellValue: (newData, value, currentRowData) => setChargePercentageValue(newData, value, currentRowData),
    ...options
  }
}

export const useCompanyColumn = ({ companies, ...options } = {}) => {
  const i18n = getI18n()
  const planningStore = usePlanningStore()
  const { archiveDisplayExpr, archivedStyle } = useArchive()

  return {
    dataField: 'company',
    caption: i18n.t('common.company'),
    lookup: useLookup({
      dataSource: companies || planningStore.companies,
      displayExpr: (i) => archiveDisplayExpr(i, 'name'),
      valueExpr: 'id',
    }),
    cellTemplate: (element, { value, displayValue }) => {
      const company = planningStore.companies.find(i => i.id === value)
      if (!company) {
        element.append(displayValue ?? '')
        return
      }
      element.appendChild(createGridLink({
        menuRoute: menuRouteEnum.COMPANIES,
        text: archiveDisplayExpr(company, 'name'),
        style: archivedStyle(company),
        onClick: () => navigateToCompany(company),
      }))
    },
    validationRules: [{ type: 'required' }],
    ...options,
  }
}

export const useCostValorizationColumn = (options) => {
  const i18n = getI18n()
  const accountingStore = useAccountingStore()
  const { setCostValorizationValue } = usePriceService({ budgetDocument: options?.budgetDocument })

  return {
    caption: i18n.t('common.costValorization'),
    dataField: 'cost_valorization',
    width: '5rem',
    lookup: useLookup({
      dataSource: {
        store: accountingStore.getLocalizedCostValorizations(),
        sort: 'name',
      },
      displayExpr: 'name',
    }),
    setCellValue: (newData, value, currentRowData) => setCostValorizationValue(newData, value, currentRowData),
    validationRules: [{ type: 'required' }]
  }
}

export const useCurrencyColumn = (options) => {
  const i18n = getI18n()
  const webSiteStore = useWebSiteStore()

  options = {
    dataField: 'currency_ref',
    caption: i18n.t('common.currency'),
    referenceField: 'price',
    editors: {},
    lookup: useLookup({
      dataSource: webSiteStore.getLocalizedCurrencies(),
      valueExpr: 'id',
      displayExpr: 'name',
    }),
    setCellValue: async(newData, value, currentRowData) => {
      if (options.convertFields) {
        const amounts = await promptCurrencyChange(
          currentRowData[options.dataField],
          value,
          Object.fromEntries(options.convertFields.map(field => [field, currentRowData[field]])),
        )
        if (!amounts) {
          newData[options.dataField] = currentRowData[options.dataField]
          return
        }
        Object.assign(newData, amounts)
      }
      newData[options.dataField] = value
      if (!value) {
        newData[options.referenceField] = null
      } else {
        const currentCurrency = webSiteStore.currencies.find((currency) => currency.id === value)
        if (currentCurrency && options?.editors) {
          options.editors[options.referenceField]?.option('format', useCurrencyOptions({ currency: currentCurrency.code }).format)
        }
      }
    },
    repaintChangesOnly: true,
    ...options
  }
  return options
}

export const useDateColumn = (options) => {
  const i18n = getI18n()
  return {...{
    dataField: 'date',
    caption: i18n.t('common.date'),
    dataType: 'date',
    width: '8rem',
  }, ...options }
}

export const useDescriptionColumn = (options) => {
  const i18n = getI18n()
  const webSiteStore = useWebSiteStore()

  return {
    caption: i18n.t('common.description'),
    dataField: 'description',
    validationRules: options?.validationRules ||
    [{
      type: 'custom',
      validationCallback: (e) => {
        if (e.data.document_row_type !== undefined) {
          return !!e.value || e.data.document_row_type !== webSiteStore.documentRowTypeEnum.COMMENT
        }
        return !!e.value
      },
      message: i18n.t('common.required'),
      reevaluate: true,
    }],
  }
}

export const useDiscountColumn = (options) => {
  const i18n = getI18n()
  const { setDiscountValue } = usePriceService()

  return {
    caption: i18n.t('common.discount'),
    ...usePercentageOptions(),
    width: '5rem',
    dataField: 'discount',
    dataType: 'number',
    setCellValue: (newData, value, currentRowData) => setDiscountValue(newData, value, currentRowData),
    ...options
  }
}

export const useDocumentRowTypeColumn = (options) => {
  const i18n = getI18n()
  const webSiteStore = useWebSiteStore()
  const { setDocumentRowTypeValue } = usePriceService()

  return {
    caption: i18n.t('common.documentRowType'),
    dataField: 'document_row_type',
    width: '8rem',
    lookup: useLookup({
      store: webSiteStore.getLocalizedDocumentRowTypes(),
      displayExpr: (i) => (i.name),
    }),
    setCellValue: (newData, value, currentRowData) => {
      options.documentRowType.value = value
      setDocumentRowTypeValue(newData, value, currentRowData)
    },
    validationRules: [{ type: 'required' }]
  }
}

export const useEndColumn = (options) => {
  const i18n = getI18n()

  return {
    dataField: 'end',
    caption: i18n.t('common.end'),
    dataType: 'datetime',
    ...options
  }
}

export const useListingColumn = (options) => {
  const i18n = getI18n()
  const planningStore = usePlanningStore()

  return {
    caption: i18n.t('common.listing'),
    dataField: 'listing',
    dataType: 'number',
    referenceField: 'currency',
    lookup: useLookup({
      dataSource: {
        store: planningStore.getListings(),
      },
      displayExpr: (i) => options?.archiveDisplayExpr(i, 'name'),
    }),
    cellTemplate: (element, rowData) => options?.archiveCellTemplate(element, rowData, planningStore.getListings().find(i => i.id === rowData.data.listing), 'name'),
    ...options}
}

export const useListingItemColumn = (options) => {
  const i18n = getI18n()
  const planningStore = usePlanningStore()
  const { archiveDisplayExpr } = useArchive()
  const { setListingItemValue } = usePriceService({ budgetDocument: options?.budgetDocument })

  const filter = []
  if (options?.listing) {
    filter.push(['listing', '=', options.listing])
  }
  if (options?.e?.isEditing) {
    filter.push(['archived', '=', false])
  }

  let currentItems = []

  const dataSourceOptions = useODataSourceOptions(planningStore.api.getAbsoluteUrl() + planningStore.api.listingItemsUrl, {
    filter: filter.length ? filter : null,
    postProcess: (items) => {
      currentItems = items
      return currentItems
    },
    sort: 'full_name',
  })

  return {
    dataField: 'listing_item',
    caption: i18n.t('common.listingItem'),
    lookup: useLookup({
      dataSource: dataSourceOptions,
      displayExpr: item => archiveDisplayExpr(item, 'full_name'),
    }),
    setCellValue: async(newData, value, currentRowData) => {
      newData[options.dataField] = value
      const item = currentItems.find(i => i.id === value)
      if (item) {
        setListingItemValue(newData, item, currentRowData, false, false)
      }
      newData.service_id = null
    },
    cellTemplate: (element, { data, displayValue }) => {
      if (data.listing_item_listing == null) {
        element.append(displayValue ?? '')
        return
      }
      element.appendChild(createGridLink({
        menuRoute: menuRouteEnum.LISTINGS,
        text: displayValue,
        onClick: () => navigateToListingItem(data.listing_item_listing, data.listing_item_code),
      }))
    },
    ...options,
  }
}

export const useNumberMnemonicColumn = () => {
  const i18n = getI18n()
  return {
    caption: i18n.t('common.number'),
    dataField: 'number_mnemonic',
    dataType: 'number',
    sortOrder: 'desc',
    width: '8rem',
  }
}

export const useIsEstimateColumn = (options) => {
  const i18n = getI18n()
  return {
    ...{
      caption: i18n.t('common.isEstimate'),
      dataField: 'is_estimate',
      dataType: 'boolean',
      cellTemplate: 'is-estimate-cell-template',
    }, ...options
  }
}

export const usePriceColumn = (options) => {
  const i18n = getI18n()
  const webSiteStore = useWebSiteStore()
  const { setPriceValue } = usePriceService()

  const defaultOptions = {
    caption: i18n.t('common.price'),
    dataField: 'price',
    dataType: 'number',
    referenceField: 'currency_ref',
    lookup: useLookup({
      valueExpr: 'id',
      displayExpr: 'name',
    }),
    setCellValue: (newData, value, currentRowData) => {
      newData[options.dataField] = value
      if (options.referenceField && !currentRowData[options.referenceField]) {
        newData[options.referenceField] = webSiteStore.getDefaultCurrency()?.id
      }
      setPriceValue(newData, value, currentRowData)
    }
  }

  options = { ...defaultOptions, ...options }

  return {
    ...useCurrencyOptions({ currency: options?.currency?.code }),
    caption: options.caption,
    dataField: options.dataField,
    dataType: options.dataType,
    calculateDisplayValue: (rowData) => useCurrencyColumnFormatter(rowData, options.dataField, options?.currency?.id || rowData[options.referenceField]),
    setCellValue: options.setCellValue,
  }
}

export const useCostColumn = (options) => {
  const i18n = getI18n()
  const webSiteStore = useWebSiteStore()
  const { setCostValue } = usePriceService()

  const defaultOptions = {
    caption: i18n.t('common.cost'),
    dataField: 'cost',
    dataType: 'number',
    referenceField: 'currency_ref',
    lookup: useLookup({
      valueExpr: 'id',
      displayExpr: 'name',
    }),
    setCellValue: (newData, value, currentRowData) => {
      newData[options.dataField] = value
      if (options.referenceField && !currentRowData[options.referenceField]) {
        newData[options.referenceField] = webSiteStore.getDefaultCurrency()?.id
      }
      setCostValue(newData, value, currentRowData)
    }
  }

  options = { ...defaultOptions, ...options }

  return {
    ...useCurrencyOptions({ currency: options?.currency?.code }),
    caption: options.caption,
    dataField: options.dataField,
    dataType: options.dataType,
    calculateDisplayValue: (rowData) => rowData[options.dataField] != null ? useCurrencyColumnFormatter(rowData, options.dataField, options?.currency?.id || rowData[options.referenceField]) : '',
    setCellValue: options.setCellValue,
  }
}

export const useQuantityColumn = () => {
  const i18n = getI18n()
  const { setQuantityValue } = usePriceService()

  return {
    caption: i18n.t('common.quantity'),
    dataField: 'quantity',
    dataType: 'number',
    width: '5rem',
    setCellValue: (newData, value, currentRowData) => setQuantityValue(newData, value, currentRowData),
    validationRules: [{ type: 'required' }]
  }
}

export const useRowNumberColumn = (options) => {
  const i18n = getI18n()

  return {
    caption: i18n.t('common.rowNumberAlt'),
    width: '5rem',
    dataField: 'row_number',
    dataType: 'number',
    sortOrder: 'asc',
    ...options
  }
}

export const useStartColumn = (options) => {
  const i18n = getI18n()

  return {
    dataField: 'start',
    caption: i18n.t('common.start'),
    dataType: 'datetime',
    ...options
  }
}

export const useTotalNoVatColumn = (options) => {
  const i18n = getI18n()

  return {
    ...useCurrencyOptions({ currency: options?.currency?.code }),
    caption: i18n.t('common.totalNoVat'),
    dataField: 'total_no_vat',
    dataType: 'number',
    width: '10rem',
    allowSorting: false,
    allowFiltering: false,
    calculateDisplayValue: (rowData) => useCurrencyColumnFormatter(rowData, 'total_no_vat', options?.currency?.id || rowData.currency_ref),
  }
}

export const useTotalVatColumn = (options) => {
  const i18n = getI18n()
  const { setTotalVatEnteredValue } = usePriceService()

  return {
    ...useCurrencyOptions({ currency: options?.currency?.code }),
    caption: i18n.t('common.totalVat'),
    dataField: 'total_vat',
    dataType: 'number',
    width: '10rem',
    allowSorting: false,
    allowFiltering: false,
    calculateDisplayValue: (rowData) => useCurrencyColumnFormatter(rowData, 'total_vat', options?.currency?.id || rowData.currency_ref),
    setCellValue: (newData, value, currentRowData) => setTotalVatEnteredValue(newData, value, currentRowData)
  }
}

const _resolveCurrencyCode = (currencyId) => {
  const webSiteStore = useWebSiteStore()
  return webSiteStore.currencies.find(c => c.id === currencyId)?.code || webSiteStore.getDefaultCurrency()?.code
}

const _formatCurrency = (value, currencyId) => {
  const { getCurrencyString } = useCurrency()
  return getCurrencyString(+value, _resolveCurrencyCode(currencyId))
}

const _marginOf = (data) => (+data.total_no_vat || 0) - (+data.cost_total || 0)

const _marginPercentageOf = (data) => {
  const totalNoVat = +data.total_no_vat || 0
  return totalNoVat ? _marginOf(data) / totalNoVat * 100 : null
}

const _costTotalClass = (data) => {
  const { isGreater } = useNumberComparison()
  return isGreater(data.cost_total, data.total_no_vat) ? 'text-negative text-bold' : null
}

const _marginClass = (data) => {
  const { isGreater, isLess } = useNumberComparison()
  const value = _marginOf(data)
  if (isGreater(value, 0)) return 'text-positive text-bold'
  if (isLess(value, 0)) return 'text-negative text-bold'
  return null
}

const _currencyCellTemplate = (getValue, options, classFn) => (element, { data }) => {
  const value = getValue(data)
  if (value == null) return
  const span = document.createElement('span')
  span.textContent = _formatCurrency(value, options?.currency?.id || data.currency_ref)
  const className = classFn?.(data)
  if (className) {
    span.className = className
  }
  element.appendChild(span)
}

export const useCostTotalColumn = (options) => {
  const i18n = getI18n()

  return {
    ...useCurrencyOptions({ currency: options?.currency?.code }),
    caption: i18n.t('common.costTotalAlt'),
    dataField: 'cost_total',
    dataType: 'number',
    width: '9rem',
    allowSorting: false,
    allowFiltering: false,
    cellTemplate: _currencyCellTemplate((data) => data.cost_total, options, _costTotalClass),
  }
}

export const useMarginColumn = (options) => {
  const i18n = getI18n()

  return {
    ...useCurrencyOptions({ currency: options?.currency?.code }),
    caption: i18n.t('common.margin'),
    dataField: 'margin',
    dataType: 'number',
    width: '9rem',
    allowSorting: false,
    allowFiltering: false,
    calculateCellValue: _marginOf,
    cellTemplate: _currencyCellTemplate(_marginOf, options, _marginClass),
  }
}

export const useMarginPercentageColumn = () => ({
  name: 'margin_percentage',
  caption: '%',
  dataType: 'number',
  width: '5rem',
  allowSorting: false,
  allowFiltering: false,
  cellTemplate: (element, { data }) => {
    const percentage = _marginPercentageOf(data)
    if (percentage == null) {
      return
    }
    const span = document.createElement('span')
    span.textContent = `${percentage.toFixed(1)}%`
    if (percentage < 0) {
      span.className = 'text-negative text-bold'
    }
    element.appendChild(span)
  },
})

export const useRateCardColumns = () => {
  const webSiteStore = useWebSiteStore()
  if (!webSiteStore.can(webSiteStore.permissionFlagEnum.RATE_CARDS_VIEW)) return []
  const allowEditing = webSiteStore.can(webSiteStore.permissionFlagEnum.RATE_CARDS_FULL)
  return [
    useUnitOfMeasureColumn({ dataField: 'rc_unit_of_measure_ref', allowEditing }),
    useCurrencyColumn({ dataField: 'rc_currency_ref', referenceField: 'rc_price', convertFields: ['rc_price', 'rc_cost'], allowEditing }),
    usePriceColumn({ dataField: 'rc_price', referenceField: 'rc_currency_ref', allowEditing }),
    useCostColumn({ dataField: 'rc_cost', referenceField: 'rc_currency_ref', allowEditing }),
    useVatTypeColumn({ dataField: 'rc_vat_type_ref', allowEditing }),
  ]
}

export const useUnitOfMeasureColumn = (options) => {
  const i18n = getI18n()
  const webSiteStore = useWebSiteStore()

  return {...{
    caption: i18n.t('common.unitOfMeasure'),
    dataField: 'unit_of_measure_ref',
    lookup: useLookup({ store: webSiteStore.getLocalizedUnitsOfMeasure() }),
  }, ...options }
}

export const useVatTypeColumn = (options) => {
  const i18n = getI18n()
  const webSiteStore = useWebSiteStore()
  const { setVatTypeValue } = usePriceService()

  const defaultOptions = {
    caption: i18n.t('common.vat'),
    dataField: 'vat_type_ref',
    lookup: useLookup({
      store: webSiteStore.getLocalizedVatTypes(),
      sort: [{ selector: 'percentage', desc: true }],
      displayExpr: 'name',
    }),
    setCellValue: (newData, value, currentRowData) => setVatTypeValue(newData, value, currentRowData),
    validationRules: [{
      type: 'custom',
      validationCallback: (e) => {
        return !!e.value || e.data.document_row_type === webSiteStore.documentRowTypeEnum.COMMENT || (!e.data.price && e.data.document_row_type === webSiteStore.documentRowTypeEnum.EXTRA)
      },
      message: i18n.t('common.required'),
      reevaluate: true,
    }]
  }

  return {...defaultOptions, ...options }
}

export const useYearMnemonicColumn = () => {
  const i18n = getI18n()

  return {
    caption: i18n.t('common.year'),
    dataField: 'year_mnemonic',
    dataType: 'number',
    sortOrder: 'desc',
    width: '6rem',
  }
}

const mountComponent = (element, component, componentProps, appContext) => {
  const vnode = h(component, componentProps)
  vnode.appContext = appContext
  render(vnode, element)
  on(element, 'dxremove', () => render(null, element))
}

const mountInformationTooltip = (element, tooltipProps, appContext) => mountComponent(element, InformationTooltip, tooltipProps, appContext)

const projectTooltipFields = (project) => {
  const i18n = getI18n()
  const planningStore = usePlanningStore()
  return [
    { key: i18n.t('common.name'), value: project.name },
    { key: i18n.t('common.listing'), value: project.listing_name },
    { key: i18n.t('common.status'), value: planningStore.getLocalizedProjectStatuses()[project.status]?.name },
  ]
}

export const createGridLink = ({ menuRoute, icon = useWebSiteStore().getMenuIcon(menuRoute), text, onClick, style = {} }) => {
  const link = document.createElement('a')
  link.href = '#'
  link.className = 'grid-link'
  Object.assign(link.style, style)
  if (icon) {
    const iconElement = document.createElement('i')
    iconElement.className = `mdi ${icon}`
    link.appendChild(iconElement)
  }
  link.appendChild(document.createTextNode(` ${text ?? ''}`))
  link.addEventListener('click', (e) => {
    e.stopPropagation()
    e.preventDefault()
    onClick()
  })
  return link
}

const createProjectLink = (project, appContext) => {
  const { archiveDisplayExpr, archivedStyle } = useArchive()
  const link = createGridLink({
    menuRoute: menuRouteEnum.PROJECTS,
    text: archiveDisplayExpr(project, 'name'),
    style: archivedStyle(project),
    onClick: () => navigateToProject(project.id),
  })
  link.classList.add('project-color-border')
  if (project.color) link.style.borderLeftColor = project.color
  mountInformationTooltip(link, { data: projectTooltipFields(project) }, appContext)
  return link
}

export const useProjectColumn = ({ fallbackText, projectOf, ...options } = {}) => {
  const i18n = getI18n()
  const planningStore = usePlanningStore()
  const { archiveDisplayExpr } = useArchive()
  const appContext = getCurrentInstance()?.appContext
  const resolveProject = projectOf ?? (({ value }) => planningStore.projects.find(i => i.id === value))

  return {
    dataField: 'project',
    caption: i18n.t('common.project'),
    width: '10rem',
    lookup: useLookup({
      dataSource: useODataSourceOptions(planningStore.api.getAbsoluteUrl() + planningStore.api.projectsUrl, { sort: 'name' }),
      valueExpr: 'id',
      displayExpr: (i) => archiveDisplayExpr(i, 'name'),
    }),
    cellTemplate: (element, cellInfo) => {
      const project = resolveProject(cellInfo)
      if (project) element.appendChild(createProjectLink(project, appContext))
      else if (fallbackText) element.append(fallbackText(cellInfo.data) ?? '')
    },
    ...options,
  }
}

export const useProjectsColumn = ({ projectsOf = row => row.projects || [], ...options } = {}) => {
  const i18n = getI18n()
  const appContext = getCurrentInstance()?.appContext

  return {
    dataField: 'projects',
    caption: i18n.t('common.project', 2),
    width: '18rem',
    allowSorting: false,
    allowFiltering: false,
    allowHeaderFiltering: false,
    calculateDisplayValue: (row) => projectsOf(row).map(project => project.name).join(', '),
    cellTemplate: (element, { data }) => {
      const projects = projectsOf(data)
      if (projects.length === 1) element.style.whiteSpace = 'normal'
      const list = document.createElement('div')
      Object.assign(list.style, { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '.125rem' })
      projects.forEach(project => list.appendChild(createProjectLink(project, appContext)))
      element.appendChild(list)
    },
    ...options,
  }
}

const budgetDocumentMnemonic = (budgetDocument) => budgetDocument
  ? [budgetDocument.year_mnemonic, budgetDocument.number_mnemonic].filter(v => v != null).join(' ')
  : ''

const budgetDocumentTooltipFields = (budgetDocument, data) => {
  const i18n = getI18n()
  const fields = [
    { key: i18n.t('common.name'), value: budgetDocument.name },
    { key: i18n.t('common.year'), value: budgetDocument.year_mnemonic },
    { key: i18n.t('common.number'), value: budgetDocument.number_mnemonic },
  ]
  if (data.total_no_vat == null) return fields

  const marginPercentage = _marginPercentageOf(data)
  return [
    ...fields,
    { key: i18n.t('common.totalNoVat'), value: _formatCurrency(data.total_no_vat, data.currency_ref) },
    { key: i18n.t('common.totalVat'), value: _formatCurrency(data.total_vat, data.currency_ref) },
    { key: i18n.t('common.costTotalAlt'), value: _formatCurrency(data.cost_total, data.currency_ref), class: _costTotalClass(data) },
    {
      key: i18n.t('common.margin'),
      value: _formatCurrency(_marginOf(data), data.currency_ref) + (marginPercentage == null ? '' : ` (${marginPercentage.toFixed(1)}%)`),
      class: _marginClass(data),
    },
  ]
}

const createBudgetDocumentLink = (budgetDocument, data, appContext) => {
  const link = createGridLink({
    menuRoute: menuRouteEnum.ACCOUNTING_BUDGET_DOCUMENTS,
    text: budgetDocumentMnemonic(budgetDocument),
    onClick: () => navigateToBudgetDocument(budgetDocument),
  })
  mountInformationTooltip(link, { data: budgetDocumentTooltipFields(budgetDocument, data) }, appContext)
  return link
}

export const useBudgetDocumentColumn = (options = {}) => {
  const i18n = getI18n()
  const appContext = getCurrentInstance()?.appContext

  return {
    dataField: 'budget_document',
    caption: i18n.t('common.budgetDocument'),
    width: '8rem',
    allowExporting: false,
    calculateGroupValue: (rowData) => budgetDocumentMnemonic(rowData.budget_document),
    cellTemplate: (element, { data }) => {
      if (!data.budget_document) return
      element.appendChild(createBudgetDocumentLink(data.budget_document, data, appContext))
    },
    ...options,
  }
}

const createCostReportLink = (projectId, text) => createGridLink({
  menuRoute: menuRouteEnum.ACCOUNTING_COST_REPORTS,
  text,
  onClick: () => navigateToProject(projectId, menuRouteEnum.ACCOUNTING_COST_REPORTS),
})

const invoiceRowCostReportText = (row) => [row.source_cost_report_project_name, row.source_cost_report_date && formatShortDate(row.source_cost_report_date)]
  .filter(Boolean)
  .join(' · ')

export const invoiceRowSourceText = (row) => {
  if (row.source_budget_document) return `${budgetDocumentMnemonic(row.source_budget_document)} · ${getI18n().t('common.rowNumberAlt')} ${row.source_budget_document_row_number}`
  if (row.source_cost_report_project != null) return invoiceRowCostReportText(row)
  return ''
}

export const useInvoiceRowSourceColumn = (options = {}) => {
  const i18n = getI18n()
  const appContext = getCurrentInstance()?.appContext

  return {
    name: 'source',
    caption: i18n.t('common.reference'),
    width: '14rem',
    allowEditing: false,
    allowSorting: false,
    allowFiltering: false,
    calculateCellValue: invoiceRowSourceText,
    cellTemplate: (element, { data }) => {
      if (data.source_budget_document) {
        element.appendChild(createBudgetDocumentLink(data.source_budget_document, {}, appContext))
        element.appendChild(document.createTextNode(` · ${i18n.t('common.rowNumberAlt')} ${data.source_budget_document_row_number}`))
      } else if (data.source_cost_report_project != null) {
        element.appendChild(createCostReportLink(data.source_cost_report_project, invoiceRowCostReportText(data)))
      }
    },
    ...options,
  }
}

const invoiceStatusClass = (status) => {
  const { invoiceStatusEnum } = useAccountingStore()
  if (status === invoiceStatusEnum.PAID) return 'text-positive'
  if (status === invoiceStatusEnum.CANCELLED) return 'text-negative'
  return null
}

const loadInvoiceTooltipFields = async(invoiceId) => {
  const i18n = getI18n()
  const accountingStore = useAccountingStore()
  const planningStore = usePlanningStore()
  const { data: invoice } = await accountingStore.fetchInvoiceByIdAsync(invoiceId)
  return [
    { key: i18n.t('common.invoiceNumber'), value: invoice.invoice_number },
    { key: i18n.t('common.date'), value: formatShortDate(invoice.date) },
    {
      key: i18n.t('common.status'),
      value: accountingStore.getLocalizedInvoiceStatus(invoice.status)?.name,
      class: invoiceStatusClass(invoice.status),
    },
    { key: i18n.t('common.company'), value: planningStore.companies.find(company => company.id === invoice.company)?.name },
    { key: i18n.t('common.totalNoVat'), value: _formatCurrency(invoice.total_no_vat, invoice.currency_ref) },
    { key: i18n.t('common.totalVat'), value: _formatCurrency(invoice.total_vat, invoice.currency_ref) },
  ]
}

export const useInvoiceColumn = (options = {}) => {
  const i18n = getI18n()
  const accountingStore = useAccountingStore()
  const appContext = getCurrentInstance()?.appContext

  return {
    dataField: 'invoice',
    caption: i18n.t('common.invoice'),
    width: '10rem',
    allowEditing: false,
    lookup: useLookup({
      dataSource: useODataSourceOptions(accountingStore.api.getAbsoluteUrl() + accountingStore.api.invoicesUrl, { sort: 'invoice_number' }),
      valueExpr: 'id',
      displayExpr: 'invoice_number',
    }),
    cellTemplate: (element, { value, displayValue }) => {
      if (value == null) return
      const link = createGridLink({
        menuRoute: menuRouteEnum.ACCOUNTING_INVOICES,
        text: displayValue,
        onClick: () => navigateToInvoice(displayValue),
      })
      mountInformationTooltip(link, { loadData: () => loadInvoiceTooltipFields(value) }, appContext)
      element.appendChild(link)
    },
    ...options,
  }
}

export const useExpenseClaimColumn = (options = {}) => {
  const i18n = getI18n()

  return {
    dataField: 'expense_claim',
    caption: i18n.t('common.expenseClaim'),
    width: '10rem',
    allowEditing: false,
    allowFiltering: false,
    allowSorting: false,
    calculateDisplayValue: (row) => row.expense_claim_number ?? '',
    cellTemplate: (element, { data }) => {
      if (!data.expense_claim) return
      element.appendChild(createGridLink({
        menuRoute: menuRouteEnum.ACCOUNTING_EXPENSE_CLAIMS,
        text: data.expense_claim_number,
        onClick: () => navigateToExpenseClaim(data.expense_claim),
      }))
    },
    ...options,
  }
}

const loadWarehouseTooltipFields = async(warehouseId) => {
  const i18n = getI18n()
  const planningStore = usePlanningStore()
  const { data: warehouse } = await planningStore.fetchWarehouseByIdAsync(warehouseId)
  const address = [
    [warehouse.street, warehouse.street_number].filter(Boolean).join(' '),
    [warehouse.zip_code, warehouse.city].filter(Boolean).join(' '),
  ].filter(Boolean).join(', ')
  return [
    { key: i18n.t('common.name'), value: warehouse.name },
    { key: i18n.t('common.address'), value: address },
    { key: i18n.t('common.phone'), value: warehouse.phone },
    { key: i18n.t('common.email'), value: warehouse.email },
  ]
}

export const useWarehouseColumn = (options = {}) => {
  const i18n = getI18n()
  const planningStore = usePlanningStore()
  const { archiveDisplayExpr } = useArchive()
  const appContext = getCurrentInstance()?.appContext

  return {
    dataField: 'destination_warehouse',
    caption: i18n.t('common.relatedLocation'),
    width: '10rem',
    lookup: useLookup({
      dataSource: useODataSourceOptions(planningStore.api.getAbsoluteUrl() + planningStore.api.warehousesUrl, { sort: 'name' }),
      valueExpr: 'id',
      displayExpr: (i) => archiveDisplayExpr(i, 'name'),
    }),
    cellTemplate: (element, { value, displayValue }) => {
      if (value == null) return
      const link = createGridLink({
        menuRoute: menuRouteEnum.WAREHOUSE,
        text: displayValue,
        onClick: () => router.push({ path: menuRoutes[menuRouteEnum.WAREHOUSE_LIST], query: { name: displayValue } }),
      })
      mountInformationTooltip(link, { loadData: () => loadWarehouseTooltipFields(value) }, appContext)
      element.appendChild(link)
    },
    ...options,
  }
}

export const useServiceColumn = (options = {}) => {
  const i18n = getI18n()
  const planningStore = usePlanningStore()
  const appContext = getCurrentInstance()?.appContext
  const dataField = options.dataField ?? 'service'

  return {
    dataField,
    caption: i18n.t('common.service'),
    width: '14rem',
    allowEditing: false,
    calculateDisplayValue: (rowData) => planningStore.getServiceName(rowData) || '',
    cellTemplate: (element, { data }) => {
      element.style.overflow = 'hidden'
      mountComponent(element, ServiceLinkTemplate, {
        serviceId: data[dataField],
        avatar: data.service_avatar_file || data.service_avatar_url,
      }, appContext)
    },
    lookup: useLookup({
      dataSource: planningStore.getServices(),
      valueExpr: 'id',
      displayExpr: 'name',
    }),
    ...options,
  }
}

export const useStaffColumn = (options = {}) => {
  const i18n = getI18n()
  const planningStore = usePlanningStore()

  return useServiceColumn({
    dataField: 'staff',
    caption: i18n.t('common.staff'),
    calculateDisplayValue: row => planningStore.getServiceName(planningStore.getStaffById(row.staff)) || '',
    lookup: useLookup({ dataSource: planningStore.staff, valueExpr: 'id', displayExpr: 'surname_name' }),
    ...options,
  })
}

export const useNotesColumn = (options) => {
  const i18n = getI18n()
  return {
    caption: i18n.t('common.note', 2),
    dataField: 'notes',
    ...options,
  }
}

// ─── Tag column helpers ────────────────────────────────────────────────────────

const _makeTagGroupCalcs = (dataField, getTagsFn) => ({
  calculateDisplayValue: (rowData) => {
    const ids = rowData[dataField]
    if (!ids?.length) return ''
    return ids.map(id => getTagsFn().find(t => t.id === id)?.name).filter(Boolean).join(', ')
  },
  groupCellTemplate: (element, info) => {
    const count = info.data.count ?? ((info.data.items?.length ?? 0) + (info.data.collapsedItems?.length ?? 0))
    const name = getTagsFn().find(t => t.id === info.data.key)?.name ?? info.data.key ?? ''
    element.textContent = count ? `${name} (${count})` : name
  },
})

/** Portal tags column (equipment/staff `tags` field). Pass `dataSource` and `getTagsFn` filtered by service type. */
export const usePortalTagsColumn = (options = {}) => {
  const i18n = getI18n()
  const portalStore = usePortalStore()
  const dataField = options.dataField ?? 'tags'
  const getTagsFn = options.getTagsFn ?? (() => portalStore.getLocalizedTags())
  return {
    dataField,
    caption: i18n.t('common.tag', 2),
    width: '12rem',
    lookup: useLookup({ dataSource: options.dataSource ?? portalStore.getLocalizedTags(), valueExpr: 'id', displayExpr: 'name' }),
    setCellValue: (newData, value) => (newData[dataField] = value),
    ..._makeTagGroupCalcs(dataField, getTagsFn),
    ...options,
  }
}

/** Service tags column (planning `tags`). */
export const useServiceTagsColumn = (options = {}) => {
  const i18n = getI18n()
  const planningStore = usePlanningStore()
  const dataField = options.dataField ?? 'tags'
  const caption = options.caption ?? i18n.t('common.tag', 2)
  const getTagsFn = options.getTagsFn ?? (() => planningStore.getLocalizedServiceTags())
  return {
    dataField,
    caption,
    width: '12rem',
    allowExporting: false,
    lookup: useLookup({ dataSource: options.dataSource ?? planningStore.getLocalizedServiceTags(), valueExpr: 'id', displayExpr: 'name' }),
    setCellValue: (newData, value) => (newData[dataField] = value),
    ..._makeTagGroupCalcs(dataField, getTagsFn),
    ...options,
  }
}

/** Project tags column (`tag_ids` field). */
export const useProjectTagsColumn = (options = {}) => {
  const i18n = getI18n()
  const planningStore = usePlanningStore()
  const dataField = options.dataField ?? 'tag_ids'
  const getTagsFn = options.getTagsFn ?? (() => planningStore.getLocalizedProjectTags())
  return {
    dataField,
    caption: i18n.t('common.tag', 2),
    width: '12rem',
    lookup: useLookup({ dataSource: options.dataSource ?? planningStore.getLocalizedProjectTags(), valueExpr: 'id', displayExpr: 'name' }),
    ..._makeTagGroupCalcs(dataField, getTagsFn),
    ...options,
  }
}

/** Service structure type column (`structure_type` field). */
export const useStructureTypeColumn = (options = {}) => {
  const i18n = getI18n()
  const portalStore = usePortalStore()
  const { dataSource, ...rest } = options
  return {
    dataField: 'structure_type',
    caption: i18n.t('common.typology'),
    width: '8rem',
    lookup: useLookup({
      dataSource: dataSource ?? portalStore.getLocalizedServiceStructureTypes(),
      valueExpr: 'id',
      displayExpr: 'name',
    }),
    cellTemplate: 'structure-type-cell-template',
    ...rest,
  }
}

/** Budget document tags column (`tag_ids` field). */
export const useBudgetDocumentTagsColumn = (options = {}) => {
  const i18n = getI18n()
  const accountingStore = useAccountingStore()
  const dataField = options.dataField ?? 'tag_ids'
  const getTagsFn = options.getTagsFn ?? (() => accountingStore.getLocalizedBudgetTags())
  return {
    dataField,
    caption: i18n.t('common.tag', 2),
    width: '12rem',
    allowSorting: false,
    lookup: useLookup({ dataSource: options.dataSource ?? accountingStore.getLocalizedBudgetTags(), valueExpr: 'id', displayExpr: 'name' }),
    ..._makeTagGroupCalcs(dataField, getTagsFn),
    ...options,
  }
}

export const useGroupsColumn = (options = {}) => {
  const i18n = getI18n()

  return {
    dataField: 'groups',
    caption: i18n.t('common.group', 2),
    cellTemplate: 'groups-cell-template',
    allowEditing: false,
    allowSorting: false,
    allowFiltering: false,
    width: '12rem',
    ...options,
  }
}

export const useServiceTypeColumn = (options) => {
  const i18n = getI18n()
  const portalStore = usePortalStore()
  return {
    dataField: 'service_type',
    caption: i18n.t('common.serviceType'),
    width: '8rem',
    allowEditing: false,
    lookup: useLookup({
      dataSource: portalStore.getLocalizedServiceTypes(),
      valueExpr: 'id',
      displayExpr: 'name',
    }),
    cellTemplate: 'service-type-cell-template',
    ...options,
  }
}
